import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import PdpSharedShippingInfoWrapper from '../PdpSharedShippingInfo/PdpSharedShippingInfoWrapper.tsx';
import { PdpSharedDetailShipsFrom } from './PdpSharedDetailShipsFrom.tsx';

import { type PdpSharedDetailShipping_item$key as ItemType } from './__generated__/PdpSharedDetailShipping_item.graphql.ts';
import { type PdpSharedDetailShipping_user$key as UserType } from './__generated__/PdpSharedDetailShipping_user.graphql.ts';

const itemFragment = graphql`
    fragment PdpSharedDetailShipping_item on Item {
        ...PdpSharedShippingInfoWrapper_item
        ...PdpSharedDetailShipsFrom_item

        isSold
        isUnpurchasable
        isMultiSku
        attributes {
            leadTime {
                option {
                    urlLabel
                }
            }
        }
    }
`;

const userFragment = graphql`
    fragment PdpSharedDetailShipping_user on User {
        ...PdpSharedShippingInfoWrapper_user
    }
`;

type Props = {
    item: ItemType;
    user?: UserType | null;
    currency: string;
    displayInline: boolean;
    preLinkQuoteDetails: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    isClient: boolean;
    postLinkNoQuoteDetails?: ReactNode;
    itemQuantity?: number | null;
};

export const PdpSharedDetailShipping: FC<Props> = ({
    item: ItemType,
    currency,
    user: UserType,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    isClient,
    itemQuantity,
}) => {
    const item = useFragment(itemFragment, ItemType);
    const user = useFragment(userFragment, UserType);
    const { isSold, isUnpurchasable, isMultiSku, attributes } = item;
    const leadTime = attributes?.leadTime?.[0]?.option?.urlLabel;

    return (
        <>
            <PdpSharedShippingInfoWrapper
                user={user}
                item={item}
                currency={currency}
                leadTime={leadTime || ''}
                renderTooltip
                isClient={isClient}
                displayInline={displayInline}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                itemQuantity={itemQuantity}
                //calculate lower shipping info only if item is regular PDP
                calculateLowerShippingInfo={!isSold && !isUnpurchasable && !isMultiSku}
                positioning="seller_details"
            >
                <PdpSharedDetailShipsFrom item={item} />
            </PdpSharedShippingInfoWrapper>
        </>
    );
};
